import React, { useEffect, useRef, useState } from 'react';
import { Music, VolumeX } from 'lucide-react';
import { assets } from '../data/mock';

/**
 * HeroMusic
 * - 30s seamless loop
 * - Tries to autoplay unmuted on mount; if the browser blocks it, falls back to
 *   starting on the first user gesture (click / scroll / keydown / touch).
 * - Volume fades with hero visibility (1.0 at top → 0 when fully out of view)
 * - Pauses when fully off-screen
 * - Minimal toggle bottom-right so users can still mute at any time
 */
const HeroMusic = () => {
  const audioRef = useRef(null);
  const sentinelRef = useRef(null);
  const [enabled, setEnabled] = useState(false); // is music currently playing
  const [available, setAvailable] = useState(false); // does the audio file exist

  // Check audio file availability without throwing 404 errors in UI
  useEffect(() => {
    let active = true;
    fetch(assets.musicTrack, { method: 'HEAD' })
      .then((r) => {
        if (active) setAvailable(r.ok);
      })
      .catch(() => active && setAvailable(false));
    return () => {
      active = false;
    };
  }, []);

  // Attempt unmuted autoplay on mount; if blocked, start on first user gesture.
  useEffect(() => {
    if (!available) return;
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = 0.6;

    let cleaned = false;
    const startOnGesture = () => {
      const a = audioRef.current;
      if (!a || cleaned) return;
      a.volume = 0.6;
      a.play().then(() => {
        setEnabled(true);
        removeListeners();
      }).catch(() => {});
    };
    const removeListeners = () => {
      cleaned = true;
      window.removeEventListener('pointerdown', startOnGesture);
      window.removeEventListener('keydown', startOnGesture);
      window.removeEventListener('scroll', startOnGesture);
      window.removeEventListener('touchstart', startOnGesture);
    };

    // Optimistic try — works in browsers that allow it (e.g. when the user has
    // already interacted with the origin recently, or has autoplay permission).
    audio.play().then(() => {
      setEnabled(true);
    }).catch(() => {
      // Blocked by autoplay policy — attach one-shot gesture listeners.
      window.addEventListener('pointerdown', startOnGesture, { once: false });
      window.addEventListener('keydown', startOnGesture, { once: false });
      window.addEventListener('scroll', startOnGesture, { once: false, passive: true });
      window.addEventListener('touchstart', startOnGesture, { once: false, passive: true });
    });

    return removeListeners;
  }, [available]);

  // Volume fade based on scroll position relative to hero
  useEffect(() => {
    if (!enabled) return;
    const handle = () => {
      const audio = audioRef.current;
      const sentinel = sentinelRef.current;
      if (!audio || !sentinel) return;
      const rect = sentinel.getBoundingClientRect();
      const heroHeight = window.innerHeight;
      // Distance from top of viewport to bottom of hero, normalised
      const distance = Math.max(0, -rect.bottom);
      const t = Math.min(1, distance / heroHeight);
      const vol = Math.max(0, 1 - t); // 1.0 → 0
      audio.volume = vol * 0.6; // cap at 60%
      if (vol === 0 && !audio.paused) audio.pause();
      if (vol > 0 && audio.paused) audio.play().catch(() => {});
    };
    handle();
    window.addEventListener('scroll', handle, { passive: true });
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle);
      window.removeEventListener('resize', handle);
    };
  }, [enabled]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (enabled) {
      audio.pause();
      setEnabled(false);
    } else {
      audio.volume = 0.6;
      audio.play().then(() => setEnabled(true)).catch(() => setEnabled(false));
    }
  };

  if (!available) return null;

  return (
    <>
      {/* Hidden sentinel — used to measure hero position */}
      <div ref={sentinelRef} className="absolute top-0 left-0 w-px h-screen pointer-events-none" />
      <audio ref={audioRef} src={assets.musicTrack} loop preload="auto" />
      <button
        onClick={toggle}
        aria-label={enabled ? 'Mute music' : 'Play music'}
        className="absolute bottom-7 right-[5.75rem] lg:right-[6.5rem] z-30 p-3 flex items-center justify-center border border-[#F5EDDD]/40 rounded-full text-[#F5EDDD] hover:bg-[#F5EDDD] hover:text-[#33200F] transition-colors"
        title={enabled ? 'Mute ambient music' : 'Play ambient music'}
      >
        {enabled ? <Music className="h-4 w-4" strokeWidth={1.5} /> : <VolumeX className="h-4 w-4" strokeWidth={1.5} />}
      </button>
    </>
  );
};

export default HeroMusic;
